import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import useBusinessResource from "../hooks/useBusinessResource";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";

const unpackProducts = (data) => {
  const products = data?.products ?? data?.produits ?? data;
  if (!Array.isArray(products)) throw new Error("La liste des produits est indisponible.");
  return products;
};

export const stockQuantity = (product) => {
  const value = product?.current_stock ?? product?.quantite_stock;
  return value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
    ? null : Number(value);
};

// Les ressources serveur restent séparées des quantités, prix et notes saisis.
export default function useStockFormResources({
  kind, open, userId, companyId, warehouseId, search = "", selectedIds = [],
}) {
  const client = useQueryClient();
  const scope = { userId, companyId, warehouseId, moduleScope: "commercial" };
  const headers = useMemo(() => ({ "X-Company-Id": String(companyId || "") }), [companyId]);
  const idsText = [...new Set(selectedIds.map(Number).filter(id => id > 0))].sort((a, b) => a - b).join(",");
  const ids = useMemo(() => idsText ? idsText.split(",").map(Number) : [], [idsText]);
  const loadProducts = useCallback(async (signal) => {
    const res = await axios.get(kind === "adjustment" ? "/api/stock-adjustments/products" : "/api/produits", {
      signal, headers, params: kind === "adjustment"
        ? { warehouse_id: warehouseId, limit: 5000 }
        : { warehouse: warehouseId, search, limit: 50, with_details: true },
    });
    return unpackProducts(res.data);
  }, [kind, headers, warehouseId, search]);
  const products = useBusinessResource({
    ...scope, resource: "products", parameters: ["stock-form", kind, search],
    enabled: Boolean(warehouseId && (kind === "adjustment" || open)), active: open, load: loadProducts,
  });
  const loadWarehouses = useCallback(async (signal) => {
    const res = await axios.get("/api/warehouses", { signal, headers, params: { company_id: companyId } });
    if (!Array.isArray(res.data?.warehouses)) throw new Error("La liste des établissements est indisponible.");
    return res.data.warehouses;
  }, [headers, companyId]);
  const warehouses = useBusinessResource({
    ...scope, warehouseId: null, scopeLevel: "company", resource: "stock-form-warehouses",
    enabled: open, active: open, load: loadWarehouses,
  });
  const loadSelected = useCallback(async (signal) => {
    const rows = [];
    for (let offset = 0; offset < ids.length; offset += 500) {
      const res = await axios.get("/api/produits/by-ids", {
        signal, headers, params: { ids: ids.slice(offset, offset + 500).join(","), warehouse_id: warehouseId },
      });
      rows.push(...unpackProducts(res.data));
    }
    return rows;
  }, [ids, headers, warehouseId]);
  const selected = useBusinessResource({
    ...scope, resource: "products", parameters: ["stock-form-selected", kind, idsText],
    enabled: open && kind === "transfer" && ids.length > 0, active: open, load: loadSelected,
  });
  const refresh = async () => {
    // La recherche n'est pas une preuve de présence des lignes déjà choisies.
    const freshProducts = kind === "transfer" && ids.length > 0
      ? await selected.refresh({ force: true }) : await products.refresh({ force: true });
    const freshWarehouses = await warehouses.refresh({ force: true });
    return { products: freshProducts, warehouses: freshWarehouses };
  };
  const invalidate = () => invalidateBusinessResources(client, scope, "product");
  return { products, warehouses, selected: kind === "adjustment" ? products : selected, refresh, invalidate };
}
